
<template>

	<div :ref="'id_' + plot_id" class="mt-2 capitalization__scatter-plot" :id="'id_' + plot_id" :style="is_window ? { height : 'calc(100% - 2.125rem - 1.5rem)' } : { height : '100%' }" v-resize v-on:resize="refresh"/>

</template>

<script>

import api from "../../../../../../api"
import * as d3 from "d3"
import lasso from "../../../../../../third_party/my_d3_lasso/lasso.js"
import { bus } from "../../../../../../api/bus"

const margin = { top : 26, right : 20, bottom : 36, left : 100 }
const padding = { top : 4 }
const legend_width = 130

export default
{
	props :
	{
		plot_id : String,
		x_attr : Object,
		y_attr : Object,
		objects : Object,
		plot_type : String,
		with_x_axis_title : { type : Boolean, default : () => false },
		set_selected : { type : Boolean, default : () => false },
		with_regression : { type : Boolean, default : () => false },
		with_zoom : { type : Boolean, default : () => false },
		with_line : { type : Boolean, default : () => false },
		with_area : { type : Boolean, default : () => false },
		is_window : { type : Boolean, default : () => false },
		window_ref : { type : Object, default : () => null },
		seq_x : { type : Array, default : () => null },
	},

	data()
	{
		return {

			tab_ready : false,

			analogues_id: '',

		}
	},

	watch :
	{
		x_attr :
		{
			immediate : true,
			deep : true,
			handler(x_attr)
			{
				this.x_attr = x_attr

				this.refresh()
			},
		},

		y_attr :
		{
			immediate : true,
			deep : true,
			handler(y_attr)
			{
				this.y_attr = y_attr

				this.refresh()
			},
		},

		plot_type :
		{
			immediate : true,
			handler(plot_type)
			{
				this.plot_type = plot_type

				this.update()
			},
		},

		with_zoom :
		{
			immediate : true,
			handler(with_zoom)
			{
				this.with_zoom = with_zoom

				this.refresh()
			},
		},
	},

	mounted()
	{
		this.tab_ready = true

		bus.$on("reset_lasso", this.reset_lasso)
		this.objects.bus.$on("refresh_plots", () => this.refresh())
		this.objects.bus.$on("update_plots", () => this.update())
		this.objects.bus.$on("toggle_objects", ev => this.$emit("toggle_objects", ev))
	},

	updated()
	{
		if(this.base_svg == null)
			this.update()
	},

	methods :
	{
		get_height()
		{
			if(this.$refs['id_' + this.plot_id] == null)
				return 1000

			return this.is_window
				? d3.select(this.$refs['id_' + this.plot_id]).node().getBoundingClientRect().height - margin.left - margin.right - this.get_scrollbar_size().height
				: d3.select(this.$refs['id_' + this.plot_id]).node().getBoundingClientRect().height - margin.top - margin.bottom - this.get_scrollbar_size().height
		},

		get_scrollbar_size()
		{
			if(this.with_zoom)
				return { height : 20, width : 20 }

			return { height : 0, width : 0 }
		},

		get_environment()
		{
			const id = "#id_" + this.plot_id
			var div = d3.select(this.$refs['id_' + this.plot_id])
			const scrollbar = this.get_scrollbar_size()
			var node = div.node()
			const width =  ( node == null ? 1000 : node.offsetWidth ) - margin.left - margin.right - scrollbar.width - legend_width
			const height = this.get_height()
			const env = { id : id, div : div, width : width, height : height }

			if(width > 0)
			{
				this.prev_env = env

				return env
			}
			else if(this.prev_env != null)
				return this.prev_env

			return env
		},

		refresh()
		{
			if(this.svg != null)
			{
				this.svg.remove()
				this.base_div.remove()

				this.svg = null
				this.base_div= null
			}

			if(this.x_attr == null || this.y_attr == null || this.plot_id == null)
				return

			if(! this.tab_ready)
				return

			const env = this.get_environment()

			if(env.width < 0)
				return

			const height = this.get_height()

			this.plot_data = this.objects.plot_two_attrs(this.x_attr, this.y_attr)

			if(this.base_div == null || this.base_div.node().getBoundingClientRect().height != height + margin.top + margin.bottom + this.get_scrollbar_size().height)
			{
				this.base_div != null && this.base_div.remove()

				const scrollbar = this.get_scrollbar_size()

				this.base_div = env.div.append("div")
					//.style("width", ( env.width + margin.left + margin.right + scrollbar.width ) + "px")
					.style("height", height + margin.top + margin.bottom + scrollbar.height + "px")

				this.legend_div = this.base_div.append("div")
					.style("width", `${legend_width}px`).style("height", `${height}px`)
					.style("margin", "0px")
					.style("float", "right")

				this.base_svg = this.base_div.append("svg")
					// .attr("width", env.width + margin.left + margin.right)
					.attr("width", `calc(100% - ${legend_width}px - ${scrollbar.width}px)`)
					.attr("height", height + margin.top + margin.bottom)
					// .attr("transform", "translate(" + scrollbar.width + ",0)")
				
				if(this.with_x_axis_title)
				{
					this.axisXTitle = this.base_div.append("div")
						.attr("class", "x label")
						.style("position", "absolute")
						.style("top", `${height + margin.top + margin.bottom*0.5}px`)
						.style("right", `${legend_width + margin.right}px`)
						.style("font-size", "1rem")
						.style("color", "var(--bft-neutral-400)")
						.text(this.x_attr.name)
					
					if (this.x_attr.key == "index")
					{
						var create_tooltip = (ev, msg) =>
						{
							var coord = d3.pointer(ev)
							// const env = this.get_environment()

							if(this.tooltip)
							{
								this.tooltip.remove()
								this.tooltip = null
							}

							this.tooltip = this.axisXTitle.append("div")
								.attr("class", "tooltip")
								.style("width", legend_width*2 + "px")
								.style("opacity", 1)
								.html(msg)
								.style("left", ( coord[0] - legend_width*2 ) + "px")
								.style("top", ( coord[1] - 63 ) + "px")
						}

						this.axisXTitle
							.classed("ind_title", true)
							.on("mouseover", (ev) => create_tooltip(ev, `
								Объекты-аналоги сортируются<br>
								по ставке капитализации<br>
								от минимальной к максимальной
							`))
							.on("mouseout", this.mouse_out)
							
						this.axisXTitle.append("div")
							.attr("class", "ind_info")
					}
				}

				this.axisYTitle = this.base_div.append("span")
					.attr("class", "y label")
					.style("position", "absolute")
					.style("left", 0)
					.style("top", 0)
					.style("font-size", "1rem")
					.style("color", "var(--bft-neutral-400)")
					.text(this.y_attr.name)

				if(this.with_zoom)
				{
					if(this.y_attr.type == "number")
					{
						this.scrollbar_y_div = this.base_div.append("div")
							.attr("class", "plot_scrollbar")
							.style("width", `${scrollbar.width}px`).style("height", `${height}px`).style("overflow-y", "auto")
							.style("margin-top", `${margin.top}px`)
							.style("float", "left")
							.on("scroll", ev => this.set_position(ev, true))
						this.scrollbar_y = this.scrollbar_y_div.append("div")
							.append("div")
							.style("width", "1px")
							.style("height", `${height}px`)
					}

					if(this.x_attr.type == "number")
					{
						this.scrollbar_x_div = this.base_div.append("div")
							.attr("class", "plot_scrollbar")
							.style("width", ( env.width ) + "px").style("height", `${scrollbar.height}px`).style("overflow-x", "auto")
							.style("margin-left", ( margin.left + scrollbar.width ) + "px")
							.style("margin-right", ( margin.right + legend_width ) + "px")
							.style("position", "absolute")
							.style("top", `${height + margin.top + margin.bottom}px`)
							.on("scroll", ev => this.set_position(ev, false))
						this.scrollbar_x = this.scrollbar_x_div.append("div")
							.style("width", `${env.width}px`)
							.style("height", "1px")
					}
				}

				this.legend_svg = this.legend_div.append("svg")
					.attr("width", legend_width)
					.attr("height", ( height + margin.top + margin.bottom ) / 2)
                .attr("transform", "translate(0, " + (( height + margin.top + margin.bottom ) / 2 - 36 ) + ")")

				this.legend_svg.append("rect")
					// .attr("x", 0)
					// .attr("y", 4)
					.attr("width", 8)
					.attr("height", 16)
					.attr("rx", 5)
					.style("fill", "var(--bft-blue-500)")
				
				this.legend_svg_analogues = this.legend_svg.append("text")
					.attr("x", 8)
					.attr("dx", 8)
					.attr("y", 10)
					.attr("dy", ".35em")
					.attr("font-size", ".75rem")
					.style("text-anchor", "start")
					.text(`Объекты-аналоги (${this.plot_data.filter(e => ! e.bad).length})`)
					.call(this.wrap, legend_width - 50)

				const bad_symbol = d3.symbol().type(d3.symbolCross).size(75)
				
				this.legend_svg.append("path")
					.attr("class", "cross")
					.attr("d", bad_symbol)
					.attr("transform", "translate(" + 5 + "," + (36+4) + ") rotate(45)");
				
				this.legend_svg_bad = this.legend_svg.append("text")
					.attr("x", 8)
					.attr("dx", 8)
					.attr("y", 8)
					.attr("dy", ".35em")
					.attr("font-size", ".75rem")
					.style("text-anchor", "start")
					.text(`Выбросы (${this.plot_data.filter(e => e.bad).length})`)
					.call(this.wrap, legend_width - 50)
					.attr("transform", "translate(0, 36)");
			}

			this.svg = this.base_svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top + padding.top})`)

			this.x_axis = this.svg.append("g")
			this.y_axis = this.svg.append("g")
			this.x = this.objects.add_axis_two_attrs(this.plot_data, this.x_axis, "x", this.x_attr, env.width, [ 0, env.width ], height, true, d3.axisBottom, { seq : this.seq_x })
			this.y = this.objects.add_axis_two_attrs(this.plot_data, this.y_axis, "y", this.y_attr, height, [ height, 0 ], env.width, false, d3.axisLeft)
			this.new_x = this.x
			this.new_y = this.y

			this.k = 1
			this.pos_x = 0
			this.pos_y = 0

			this.svg.selectAll("g.tick")
				.style("stroke-width", 0.1)
				.style("font-size", 16)
				.style("color", "var(--bft-neutral-400)")

			this.scatter_background = this.svg.append("svg").attr("width", env.width).attr("height", height)
			this.scatter = this.scatter_background.append("g").attr("width", env.width).attr("height", height)

			if(this.with_zoom)
			{
				this.zoom = d3.zoom().on("zoom", ev => this.set_zoom(ev)).filter((ev) => ev.shiftKey)
			}

			if(this.with_regression)
			{
				this.trendline = this.scatter.append("g")

				this.ls = this.linear_regression(this.plot_data.filter(e => !e.bad))
				this.$emit('get_regression', this.ls)
                
				this.trendline.append("line")
					.attr("class", "trendline")
					.attr("x1", this.x(d3.min(this.plot_data, d => d.x)))
					.attr("y1", this.y(this.ls.calc(d3.min(this.plot_data, d => d.x))))
					.attr("x2", this.x(d3.max(this.plot_data, d => d.x)))
					.attr("y2", this.y(this.ls.calc(d3.max(this.plot_data, d => d.x))))
					.attr("stroke", "var(--bft-blue-500)")
					.attr("stroke-width", 1)
					.style("stroke-dasharray", ("3, 3"))
            
				this.trendline.append("text")
					.attr("x", margin.left + 8)
					.attr("y", margin.top + 16)
					.style("font-size", ".875rem")
					.style("font-weight", "300")
					.text(`Y = ${this.ls.slope.toFixed(4)}x ${this.ls.intercept >= 0
						? "+ " + Math.round(this.ls.intercept)
						: "- " + Math.abs(Math.round(this.ls.intercept))
					}`)
					
				this.trendline.append("text")
					.attr("x", margin.left + 8)
					.attr("y", margin.top + 32)
					.style("font-size", ".875rem")
					.style("font-weight", "300")
					.text(`R² = ${Number((this.ls.r2).toFixed(2))}`)
			}

			if(this.with_zoom)
			{
				this.zoom_rect = this.scatter
					.append("rect")
					.attr("width", env.width)
					.attr("height", height)
					.style("fill", "none")
					.style("pointer-events", "all")
					.call(this.zoom)

				const add_zoom_event = (ev) => {
					if (ev.key == "Shift") { this.zoom_rect.raise() }
				}
				const delete_zoom_event = (ev) => {
					if (ev.key == "Shift") { this.zoom_rect.lower() }
				}

				this.scatter.on("mouseenter", () => {
					if (this.is_window)
					{
						this.window_ref.window.addEventListener("keydown", add_zoom_event)
						this.window_ref.window.addEventListener("keyup", delete_zoom_event)
					}
					else
					{
						window.addEventListener("keydown", add_zoom_event)
						window.addEventListener("keyup", delete_zoom_event)
					}
				})

				this.scatter.on("mouseleave", () => {
					if (this.is_window)
					{
						this.window_ref.window.removeEventListener("keydown", add_zoom_event)
						this.window_ref.window.removeEventListener("keyup", delete_zoom_event)
					}
					else
					{
						window.removeEventListener("keydown", add_zoom_event)
						window.removeEventListener("keyup", delete_zoom_event)
					}
					this.zoom_rect.lower()
				})
			}

			if (this.with_area)
			{
				const area_gradient = this.scatter.append("defs").append("linearGradient")
					.attr("id", "mygrad")
					.attr("x1", "0%")
					.attr("x2", "0%")
					.attr("y1", "0%")
					.attr("y2", "100%")
	
				area_gradient
					.append("stop")
						.attr("offset", "0%")
						.style("stop-color", "var(--bft-blue-500)")
						.style("stop-opacity", 1)
	
				area_gradient            
					.append("stop")
						.attr("offset", "100%")
						.style("stop-color", "#ffffff")
						.style("stop-opacity", 1)
	
				var area = d3.area()
					.x(obj => this.new_x(obj.x))
					.y0(height)
					.y1((obj) => this.new_y(obj.y))
					.curve(d3.curveMonotoneX)
	
				this.scatter.append("path")
					.attr("class", "area")
					.attr("fill", "url(#mygrad)")
					.attr("fill-opacity", .3)
					.attr("stroke", "none")
					.attr("d", area(this.plot_data))
			}

			if (this.with_line)
			{
				var line = d3.line()
					.x(obj => this.new_x(obj.x))
					.y(obj => this.new_y(obj.y))
					.curve(d3.curveMonotoneX)
	
				this.scatter.append("path")
					.attr("class", "line")
					.attr("fill", "none")
					.attr("d", line(this.plot_data))
					.style("stroke", "var(--bft-blue-500)")
					.style("stroke-width", "2")
			}
			
			this.points = this.scatter.selectAll("circle").data(this.plot_data).enter().append("circle")
				.attr("obj_id", obj => obj.id)
				.attr("cx", obj => this.x(obj.x))
				.attr("cy", obj => this.y(obj.y))
				.attr("r", 6)
				.style("stroke", "#ffffff")
				.on("click", this.point_click)
				.on("mouseover", this.mouse_over)
				.on("mouseout", this.mouse_out)

			this.add_lasso()
			this.update()
		},

        wrap(text, width) {
            text.each(function () {
                var text = d3.select(this),
                    words = text.text().split(/\s+/).reverse(),
                    word,
                    line = [],
                    lineNumber = 0,
                    lineHeight = 1.1, // ems
                    x = text.attr("x"),
                    y = text.attr("y"),
                    dy = 0, //parseFloat(text.attr("dy")),
                    tspan = text.text(null)
                                .append("tspan")
                                .attr("x", x)
                                .attr("y", y)
                                .attr("dy", dy + "em");
                
                while ((word = words.pop())) {
                    line.push(word);
                    ++lineNumber
                    tspan.text(line.join(" "));
                    if (tspan.node().getComputedTextLength() > width) {
                        line.pop();
                        tspan.text(line.join(" "));
                        line = [word];
                        tspan = text.append("tspan")
                                    .attr("x", (x * lineNumber))
                                    .attr("y", y)
                                    .attr("dy", (lineNumber-1) * lineHeight + dy + "em")
                                    .text(word);
                    }
                }
            });
        },

        linear_regression(data)
        {
            var x_values = data.map(e => e.x)
            var y_values = data.map(e => e.y)
            //Create the regressor object to store the equation's data
            var regressor = {};

            //Set variables we'll need to get the slope and intercept; we need to find the equation in the format y = m*x+b where m is the slope and b is the intercept
            var x_mean = x_values.reduce((a, b) => a + b, 0)/x_values.length;
            var y_mean = y_values.reduce((a, b) => a + b, 0)/y_values.length;

            //Equations to solve for slope: 
            var slope = 0, slope_numerator = 0, slope_denominator = 0;
            for(var i = 0; i < x_values.length; i++)
            {
                slope_numerator += (x_values[i]-x_mean)*(y_values[i]-y_mean);
                slope_denominator += Math.pow((x_values[i]-x_mean),2);
            }

            slope = slope_numerator/slope_denominator;

            //Add the slope's value to the regressor object
            regressor['slope'] = slope;

            //Equation to solve for intercept
            var intercept = y_mean - x_mean*slope;

            //Store it's value in the regressor object
            regressor['intercept'] = intercept;

            
            //Get y_hat, or predicted values of y based on x_values
            //Loop through x_values, and run the elements through the lr equation to get predictions
            var y_hat = [];
            for(i=0; i<x_values.length; i++)
            {
                y_hat.push(x_values[i]*regressor['slope']+regressor['intercept']);
            }

            //Add to regressor object
            regressor['y_hat'] = y_hat;

            //Now to find the r2 score
            var residual_sum_of_squares = 0, total_sum_of_squares = 0, r2 = 0;

            for(i=0; i<y_values.length; i++)
            {
                residual_sum_of_squares+= Math.pow((y_values[i] - y_hat[i]),2)
                total_sum_of_squares += Math.pow((y_values[i]-y_mean),2)
            }

            r2 = 1 - residual_sum_of_squares/total_sum_of_squares;

            //Add to regressor object
            regressor['r2'] = r2;

            regressor['calc'] = function (x)
            {
                return x * this.slope + this.intercept
            }

            return regressor
        },

		add_lasso()
		{
			if(api.check_roles(this, [ "model" ]))
			{
				const env = this.get_environment()
				const height = this.get_height()

				this.lasso = lasso()
					.closePathSelect(true)
					.targetArea(this.scatter)
					.items(this.points)
					.width(env.width)
					.height(height)
					.on("end", this.lasso_end)
			
				this.svg.call(this.lasso)
			}
		},

		point_click(ev, obj)
		{
			const coord = d3.pointer(ev)
			const coord_x = coord[0]
			const coord_y = coord[1]
			const selection = this.scatter.selectAll("circle").filter(
				_obj =>
				{
					var d = 0
					var not_equal = false

					if(_obj.id != obj.id)
					{
						if(this.x_attr.type == "number")
						{
							const dx = this.x(_obj.x) - coord_x

							if(this.y_attr.type == "number")
							{
								const dy = this.y(_obj.y) - coord_y

								d = Math.sqrt(dx * dx + dy * dy) / 2
							}
							else if(_obj.y == obj.y)
								d = Math.abs(dx)
							else
								not_equal = true
						}
						else if(_obj.x == obj.x)
						{
							if(this.y_attr.type == "number")
								d = Math.abs(this.y(_obj.y) - coord_y)
							else if(_obj.y != obj.y)
								not_equal = true
						}
						else
							not_equal = true

					}

					return _obj != null && ( ! not_equal && d < 5 ) && this.scatter.select("circle[obj_id='" + _obj.id + "']").style("opacity") != 0
				})

			this.objects.toggle(selection, ev.ctrlKey, this.set_selected)
		},

		mouse_over(ev, obj)
		{
			var coord = d3.pointer(ev)
			// const env = this.get_environment()

			if(this.tooltip)
			{
				this.tooltip.remove()
				this.tooltip = null
			}

			var elem = this.scatter.select("circle[obj_id='" + obj.id + "']")

			if(elem.style("opacity") == 0)
				return

			// this.tooltip = this.base_div.append("div")
			this.tooltip = this.base_div.append("div")
				.attr("class", "tooltip")
				.style("opacity", 1)
				.html(`${this.x_attr.name}: ${api.round2(obj.x)}<br/>${this.y_attr.name}: ${api.roundToStr(obj.y, 4)}`)
				.style("left", ( coord[0] + margin.left + margin.right ) + "px")
				.style("top", ( coord[1] + margin.top + margin.bottom ) + "px")
		},

		mouse_out()
		{
			if(this.tooltip)
				this.tooltip.remove()
		},

		set_position(ev, is_y)
		{
			if(is_y)
			{
				const raw_pos = ev.srcElement.scrollTop
				const raw_height = ev.srcElement.scrollHeight
				const pos = raw_pos / raw_height
				const base_coord_0 = this.new_y(this.y_attr.min - this.y_attr.d)
				const distance = this.new_y(this.y_attr.max + this.y_attr.d) - base_coord_0
				const pos_distance = pos * distance

				this.pos_y = pos_distance
				ev.transform = new d3.ZoomTransform(this.k, this.pos_x, this.pos_y)
				this.set_zoom(ev, true)
			}
			else
			{
				const raw_pos = ev.srcElement.scrollLeft
				const raw_width = ev.srcElement.scrollWidth
				const pos = raw_pos / raw_width
				const base_coord_0 = this.new_x(this.x_attr.min - this.x_attr.d)
				const distance = this.new_x(this.x_attr.max + this.x_attr.d) - base_coord_0
				const pos_distance = pos * distance
				
				this.pos_x = - pos_distance
				ev.transform = new d3.ZoomTransform(this.k, this.pos_x, this.pos_y)
				this.set_zoom(ev, true)
			}
		},

		set_zoom(ev, set_position = false)
		{
			if(set_position)
			{
				if(ev.transform.x == null)
					ev.transform.x = 0

				if(ev.transform.y == null)
					ev.transform.y = 0

				this.pos_x = ev.transform.x
				this.pos_y = ev.transform.y
			}
			else
			{
				if(ev.sourceEvent.type != "wheel")
					return

				if(ev.sourceEvent.wheelDelta > 0)
					ev.transform.k += 0.001
				else
					ev.transform.k -= 0.001

				if(ev.transform.k < 1)
				{
					ev.transform.k = 1
					ev.transform.x = 0
					ev.transform.y = 0

					this.objects.new_domain_for_axis_two_attrs(this.plot_data, this.x, "x", this.x_attr, this.plot_type == "Все")
					this.objects.new_domain_for_axis_two_attrs(this.plot_data, this.y, "y", this.y_attr, this.plot_type == "Все")
				}

				this.k = ev.transform.k
			}

			const env = this.get_environment()
			const height = this.get_height()

			this.new_x = this.x
			this.new_y = this.y

			if(this.x_attr.type == "number")
			{
				this.new_x = ev.transform.rescaleX(this.x).interpolate(d3.interpolateRound)

				if (this.x_attr.key == "index")
				{
					this.x_axis.call(d3.axisBottom(this.new_x).ticks(10).tickValues(this.new_x.ticks().filter(Number.isInteger)).tickFormat(value => api.format_tick_label(this.x_attr, value)).tickSize(-height))
						.call(g => g.selectAll(".domain").remove()) // Удаляет рамку
				}
				else
				{
					this.x_axis.call(d3.axisBottom(this.new_x).ticks(10).tickFormat(value => api.format_tick_label(this.x_attr, value)).tickSize(-height))
						.call(g => g.selectAll(".domain").remove()) // Удаляет рамку
				}

				if(! set_position)
				{
					const from = this.new_x(this.x_attr.min - this.x_attr.d)
					const to = this.new_x(this.x_attr.max + this.x_attr.d)
					const diff = Math.abs(to - from)
					const new_width = Math.max(diff, env.width)
					const mouse = ev.sourceEvent.offsetX - margin.left
					const mouse_prepared = this.new_x.invert(mouse)
					const coef = mouse_prepared / ( this.x_attr.max - this.x_attr.min )
					const pos = new_width * coef

					if(this.with_zoom)
					{
						this.scrollbar_x_div.property("scrollLeft", pos)
						this.scrollbar_x.style("width", `${new_width}px`)
					}
				}
			}

			if(this.y_attr.type == "number")
			{
				this.new_y = ev.transform.rescaleY(this.y).interpolate(d3.interpolateRound)
				this.y_axis.call(d3.axisLeft(this.new_y).ticks(10).tickFormat(value => api.format_tick_label(this.y_attr, value)).tickSize(-env.width))
					.call(g => g.selectAll(".domain").remove()) // Удаляет рамку

				if(! set_position)
				{
					const from = this.new_y(this.y_attr.min - this.y_attr.d)
					const to = this.new_y(this.y_attr.max + this.y_attr.d)
					const diff = Math.abs(to - from)
					const new_height = Math.max(diff, height)
					const mouse = ev.sourceEvent.offsetY
					const mouse_prepared = this.new_y.invert(mouse)
					const coef = 1 - mouse_prepared / this.y_attr.max
					const pos = new_height * coef

					if(this.with_zoom)
					{
						this.scrollbar_y_div.property("scrollTop", pos)
						this.scrollbar_y.style("height", `${new_height}px`)
					}
				}
			}

			this.scatter.selectAll("circle")
				.attr("cx", obj => this.new_x(obj.x))
				.attr("cy", obj => this.new_y(obj.y))
			this.scatter_background.selectAll(".cross")
				.attr("transform", obj => this.bad_symbol_transform(obj.x, obj.y))

			this.svg.selectAll("g.tick")
				.style("stroke-width", 0.1)
				.style("font-size", 16)
				.style("color", "var(--bft-neutral-400)")
			
			if (this.with_area)
			{
				var new_area = d3.area()
					.x(obj => this.new_x(obj.x))
					.y0(height)
					.y1((obj) => this.new_y(obj.y))
					.curve(d3.curveMonotoneX)
	
				this.scatter.select(".area").attr("d", new_area(this.plot_data))
			}
			
			if (this.with_line)
			{
				var new_line = d3.line()
					.x(obj => this.new_x(obj.x))
					.y(obj => this.new_y(obj.y))
					.curve(d3.curveMonotoneX)
				
				this.scatter.select(".line").attr("d", new_line(this.plot_data))
			}

			if(this.with_regression)
			{
				this.trendline.select(".trendline")
					.attr("x1", this.new_x(d3.min(this.plot_data, d => d.x)))
					.attr("y1", this.new_y(this.ls.calc(d3.min(this.plot_data, d => d.x))))
					.attr("x2", this.new_x(d3.max(this.plot_data, d => d.x)))
					.attr("y2", this.new_y(this.ls.calc(d3.max(this.plot_data, d => d.x))))
			}
		},

		bad_symbol_transform(x, y)
		{
			const _x = this.new_x(x)
			const _y = this.new_y(y)

			return `translate(${_x}, ${_y}) rotate(45)`
		},

		update()
		{
			if(this.scatter)
			{
				var selection = this.scatter.selectAll("circle").filter(obj => obj != null)

				this.legend_svg_analogues
					.text(`Объекты-аналоги (${this.plot_data.filter(e => ! e.bad).length})`)
					.call(this.wrap, legend_width - 50)
				this.legend_svg_bad
					.text(`Выбросы (${this.plot_data.filter(e => e.bad).length})`)
					.call(this.wrap, legend_width - 50)

				selection.style("fill", obj => this.objects.color(obj.id)) 

				if(this.plot_type == "Все")
				{
					selection.filter(obj => ! this.objects.bad(obj.id)).style("opacity", "100%")
					selection.filter(obj => this.objects.bad(obj.id)).style("opacity", "1%")
				}
				else if(this.plot_type == "Не выбросы")
				{
					selection.filter(obj => ! this.objects.bad(obj.id)).style("opacity", "100%")
					selection.filter(obj => this.objects.bad(obj.id)).style("opacity", "0%")
				}
				else
				{
					const is_teach = this.plot_type == "Обучающая выборка"

					selection.filter(obj => obj.is_teach == is_teach && ! this.objects.bad(obj.id)).style("opacity", "100%")
					selection.filter(obj => obj.is_teach != is_teach || this.objects.bad(obj.id)).style("opacity", "0%")
				}

				this.scatter_background.selectAll(".cross").remove()

				if(this.plot_type == "Все")
				{
					var plot_data_crosses = this.plot_data.filter(obj => this.objects.bad(obj.id))
					const bad_symbol = d3.symbol().type(d3.symbolCross).size(75)

					var crosses = this.scatter.append("g").attr("class", "crosses")

					crosses.selectAll(".cross").data(plot_data_crosses).enter()
						.append("path")
						.attr("class", "cross")
						.attr("d", bad_symbol)
						.style("fill", obj => this.objects.selected(obj.id) ? "gray" : "black")
						.attr("transform", obj => this.bad_symbol_transform(obj.x, obj.y))
						.on("click", this.point_click)

					selection.raise()
				}

				if (this.with_area)
				{
					var new_area = d3.area()
						.x(obj => this.new_x(obj.x))
						.y0(this.get_height())
						.y1((obj) => this.new_y(obj.y))
						.curve(d3.curveMonotoneX)
					
					this.scatter.select(".area").attr("d", new_area(this.plot_data))
				}
				
				if (this.with_line)
				{
					var new_line = d3.line()
						.x(obj => this.new_x(obj.x))
						.y(obj => this.new_y(obj.y))
						.curve(d3.curveMonotoneX)
	
					this.scatter.select(".line").attr("d", new_line(this.plot_data))
				}

				if(this.with_regression)
				{
					this.trendline.select(".trendline")
						.attr("x1", this.new_x(d3.min(this.plot_data, d => d.x)))
						.attr("y1", this.new_y(this.ls.calc(d3.min(this.plot_data, d => d.x))))
						.attr("x2", this.new_x(d3.max(this.plot_data, d => d.x)))
						.attr("y2", this.new_y(this.ls.calc(d3.max(this.plot_data, d => d.x))))
				}

				if(this.k < 1.00001)
				{
					this.objects.new_domain_for_axis_two_attrs(this.plot_data, this.x, "x", this.x_attr, this.plot_type == "Все")
					this.objects.new_domain_for_axis_two_attrs(this.plot_data, this.y, "y", this.y_attr, this.plot_type == "Все")

					this.pos_x = 0
					this.pos_y = 0

					var ev = { sourceEvent : { offsetX : 0, offsetY : 0 } }
					ev.transform = new d3.ZoomTransform(this.k, this.pos_x, this.pos_y)

					this.set_zoom(ev, true)
				}

			}

			this.$emit('get_plot_data', this.plot_data)
		},

		lasso_end(ev)
		{
			const selection = this.lasso.selectedItems().filter(
				_obj =>
				{
					if(_obj)
					{
						const elem = this.scatter.select("circle[obj_id='" + _obj.id + "']")

						return elem.style("opacity") != 0
					}

					return false
				})

			this.objects.toggle(selection, ev.sourceEvent.ctrlKey, this.set_selected)
        },

		reset_lasso()
		{
			if(this.svg)
			{
				this.svg.selectAll(".lasso").remove()
				this.add_lasso()
			}
		},
	}
}

</script>

<style>

.capitalization__scatter-plot .tooltip
{
	background-color : var(--bft-neutral-900-tooltip);
	box-shadow: 0 10px 15px 0 var(--bft-neutral-500-tooltip) !important;
    border-radius : .25rem;
	color: #ffffff;
	opacity: 0;
	padding: .25rem .5rem;
	font-family: inherit;
}

.capitalization__scatter-plot:hover .plot_scrollbar::-webkit-scrollbar-thumb
{
	background-color: var(--bft-neutral-300) !important;
}
@-moz-document url-prefix() {
	.capitalization__scatter-plot:hover .plot_scrollbar
	{
		scrollbar-color: var(--bft-neutral-300) transparent;
	}
}

.lasso path
{
	stroke: rgb(80,80,80);
    stroke-width: 2px;
}

.lasso .drawn
{
	fill-opacity: 0.05;
}

.lasso .loop_close
{
	fill: none;
	stroke-dasharray: 4, 4;
}

.lasso .origin
{
	fill: rgb(51, 153, 255);
	fill-opacity: 0.5;
}

</style>

<style scoped>
* >>> .ind_info
{
    background-color: var(--bft-neutral-400);
    -webkit-mask-image: url('~@/assets/icons/info.svg');
    mask-image: url('~@/assets/icons/info.svg');
    width: 1.5rem;
    height: 1.5rem;
}

* >>> .ind_title
{
	display: flex;
	align-items: center;
	gap: .25rem;
}
</style>

